// ============================================================================
// 建物データ取得 / 自前ビルドPMTiles
// ----------------------------------------------------------------------------
// OSM Overpass APIは無料公開ミラーの混雑・レート制限で不安定なため、建物
// フットプリントは自前ビルドしたPMTiles(z12単一ズーム、tippecanoeで間引き
// なし)から取得する。単一ズームのHTTPレンジリクエストのみで完結するため、
// Overpassのような混雑・タイムアウトが起きない。
// ビルド手順・検証結果は WORKLOG.md 参照。
// ============================================================================

import { VectorTile } from "@mapbox/vector-tile";
import { PbfReader } from "pbf";
import { distanceMeters } from "./geo";
import type { Building, LngLat } from "./types";
import {
  TILE_ZOOM,
  getPMTiles,
  localToLngLat,
  ringArea,
  ringIntersectsBbox,
  tileBboxDistance,
  tilesForBbox,
} from "./pmtilesCommon";

/** Cloudflare R2(パブリックバケット、CORS設定済み)でホスティング。 */
const TILE_URL = "https://pub-aac031ce777c439bbb845a5887dd9b21.r2.dev/japan-buildings.pmtiles";

// 都心などの高密度エリアでは1タイル(z12、約8km四方)だけで18万件を超える
// 建物を含むことがあり、それを複数タイル分メインスレッドで同期デコードすると
// UIスレッドが長時間ブロックされてタブごとクラッシュする(実測)。呼び出し側
// でも近い順に間引くが、その前段としてデコード自体の総量も頭打ちにする。
//
// 注意: この上限に達したタイルを「タイル内のフィーチャー走査順」でそのまま
// 打ち切ると、走査順は打ち上げ地点からの距離と無関係(tippecanoeの内部順)
// なので、肝心の打ち上げ地点周辺が丸ごと欠落しうる(実際にさいたま市花火
// 大会で発生した不具合)。そのため単純な件数打ち切りではなく、打ち上げ
// 地点(=bbox中心)に近いタイルから処理し、収集した候補全体を距離でソート
// してから上限件数を残す方式にする。
//
// 上限値は「打ち上げ地点を含むタイル+隣接タイル数枚が最大密度(実測18万件
// 超/枚)でも、その全部をデコードし切れる」余裕を持たせてある。以前は
// 6万件に抑えていたが、それだと「打ち上げ地点のタイル+隣タイルだけで
// 上限に達し、本当は近い別の隣接タイル(密集地帯)が一度もデコードされない
// まま打ち切られる」不具合があった(実例: 江戸川区花火大会で、隣接する
// 高密度タイルが処理順5番目になり読まれずに終わっていた)。
const MAX_DECODED_FEATURES = 300000;

/** OSMタグ相当のプロパティから建物高さ(m)を推定する（overpass.tsの旧parseHeightと同じ規則）。 */
function parseHeight(props: Record<string, unknown>): { height: number; estimated: boolean } {
  const h = parseFloat(String(props.height ?? ""));
  if (!isNaN(h) && h > 0) return { height: h, estimated: false };
  const lv = parseFloat(String(props["building:levels"] ?? ""));
  if (!isNaN(lv) && lv > 0) return { height: lv * 3.3, estimated: false };
  return { height: 9, estimated: true };
}

/**
 * bbox [south, west, north, east] にかかるz12タイルをすべて取得し、
 * buildingレイヤーをBuilding[]に変換する。1棟が複数リング(穴・複数パーツ)を
 * 持つ場合は面積最大のリングのみを採用する（Overpass経由の旧実装がway単位=
 * 単一リングしか扱わなかったのに合わせた簡略化）。
 */
export async function fetchBuildingsFromTiles(
  south: number,
  west: number,
  north: number,
  east: number
): Promise<Building[]> {
  const p = getPMTiles(TILE_URL);
  const center: LngLat = [(west + east) / 2, (south + north) / 2];
  const tileCoords = tilesForBbox(south, west, north, east, TILE_ZOOM).sort(
    (a, b) =>
      tileBboxDistance(center, a.tx, a.ty, TILE_ZOOM) -
      tileBboxDistance(center, b.tx, b.ty, TILE_ZOOM)
  );

  // タイル本体の取得(I/O)は並列に行い、デコード(CPU)だけを打ち上げ地点に
  // 近いタイルから順に処理する(下のfor文)。
  const tileBytes = await Promise.all(tileCoords.map(({ tx, ty }) => p.getZxy(TILE_ZOOM, tx, ty)));

  const candidates: (Building & { dist: number })[] = [];
  let nextId = 1;
  for (let t = 0; t < tileCoords.length; t++) {
    // 既に上限を十分満たしていれば、これ以降(=より遠い)タイルのデコードは
    // 打ち切ってよい。近い順に処理しているため、遠いタイルの候補が採用
    // される可能性は低い。
    if (candidates.length >= MAX_DECODED_FEATURES) break;
    const res = tileBytes[t];
    if (!res) continue;
    const { tx, ty } = tileCoords[t];
    const tile = new VectorTile(new PbfReader(new Uint8Array(res.data)));
    const layer = tile.layers.buildings;
    if (!layer) continue;
    for (let i = 0; i < layer.length; i++) {
      const f = layer.feature(i);
      const rings = f
        .loadGeometry()
        .map((pts) =>
          pts.map((pt): LngLat => localToLngLat(pt.x, pt.y, tx, ty, TILE_ZOOM, f.extent))
        )
        .filter((ring) => ring.length >= 3);
      if (rings.length === 0) continue;
      const outer = rings.reduce((a, b) => (ringArea(b) > ringArea(a) ? b : a));
      if (!ringIntersectsBbox(outer, south, west, north, east)) continue;
      const { height, estimated } = parseHeight(f.properties);
      candidates.push({
        id: nextId++,
        ring: outer,
        height,
        estimated,
        dist: distanceMeters(center, outer[0]),
      });
    }
  }
  candidates.sort((a, b) => a.dist - b.dist);
  return candidates.slice(0, MAX_DECODED_FEATURES).map(({ dist: _dist, ...b }) => b);
}
